import { useEffect } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { Text } from '@belong/ui';
import { ALL_DISCLOSURES_LIST_ENUMS_AND_TITLES } from 'accounts/constants/home-disclosures';
import { HOME_DISCLOSURES_STRINGS } from 'accounts/constants/strings/home-disclosures.strings';
import { CheckboxFinalFormAdapter } from 'components/Checkbox/Checkbox';
import { DateInputFinalFormAdapter } from 'components/DateInput/DateInput';
import Field from 'components/Field/Field';
import { InputFinalFormAdapter } from 'components/Input/Input';
import {
  maskCurrency,
  maskMMDDYYYY,
  unmaskMMDDYYYY,
  maskYYYY,
  unmaskYYYY,
  unmaskNumber,
} from 'components/Input/masks';
import {
  MEDIA_LABEL,
  NewMediaUploadAdapter,
  validateMedia,
} from 'components/NewMediaUploader/NewMediaUploader';
import {
  SelectorFinalFormAdapter,
  SELECTOR_TYPES,
  GRID_SIZE,
} from 'components/Selector/Selector';
import { YesNoFinalFormAdapter } from 'components/Selector/YesNoFinalFormAdapter/YesNoFinalFormAdapter';
import { Col, Row } from 'forkedlibraries/react-bootstrap';
import StandardFieldArray from 'formcomponents/StandardFieldArray/StandardFieldArray';
import {
  composeValidators,
  dateValidationMMDDYY,
  integer,
  required,
} from 'utils/validation';
import { HousingCodeViolationFields } from './housing-code-violation-fields';

export function DisclosureFormField({
  item,
  region,
  state,
  propertyYearBuilt,
}: {
  item:
    | (typeof ALL_DISCLOSURES_LIST_ENUMS_AND_TITLES)[number]
    | { name: string; title: string };
  region: string;
  state: string;
  propertyYearBuilt?: number;
}) {
  const formState = useFormState();
  const form = useForm();

  // Whether to reveal the free-text "Other" notice detail is derived directly
  // from the "Other" checkbox in form state (covers both the initial saved
  // value and live changes), so no sync effect or local state is needed.
  const showOtherNoticesDetail =
    !!formState.values?.disclosures?.types?.[item.name]?.Other;

  // StandardFieldArray only renders its "add" control on the last existing row,
  // so an empty housing-code violations list would offer no way to add the
  // first one. Seed a single row on mount (the first row is not removable).
  const housingCodeViolations =
    formState.values?.disclosures?.types?.HousingCodeViolations
      ?.housingCodeViolations;

  useEffect(() => {
    if (
      item.name === 'HousingCodeViolations' &&
      !housingCodeViolations?.length
    ) {
      form?.mutators?.push?.(
        'disclosures.types.HousingCodeViolations.housingCodeViolations',
        {},
      );
    }
  }, [item.name, housingCodeViolations, form]);

  return (
    <Col md={24}>
      {item.name === 'PropertyYearBuilt' && (
        <Field
          validate={composeValidators(required, integer)}
          placeholder="Year (YYYY)"
          mask={maskYYYY}
          unmask={unmaskYYYY}
          component={InputFinalFormAdapter}
          name="propertyYearBuilt"
        />
      )}

      {item.name === 'HasSepticSystem' && (
        <Field
          validate={composeValidators(required, integer)}
          placeholder={HOME_DISCLOSURES_STRINGS.permitted_bedrooms}
          component={InputFinalFormAdapter}
          name={`disclosures.types.${item.name}.bedroomsCountPermitted`}
          type="number"
        />
      )}

      {item.name === 'IsNearMilitaryAirInstallation' && (
        <>
          <Text className="mt-xl block font-semibold">
            {HOME_DISCLOSURES_STRINGS.noise_zone}
          </Text>
          <Field
            validate={required}
            component={SelectorFinalFormAdapter}
            type={SELECTOR_TYPES.SMALLTEXTBUTTON}
            grid
            gridSize={GRID_SIZE.MEDIUM}
            name={`disclosures.types.${item.name}.amountOfDecibels`}
            buttons={[
              {
                label: HOME_DISCLOSURES_STRINGS.less_than_65_decibels,
                key: 'LessThan65',
              },
              {
                label: HOME_DISCLOSURES_STRINGS.between_65_and_70_decibels,
                key: 'Between65And70',
              },
              {
                label: HOME_DISCLOSURES_STRINGS.between_70_and_75_decibels,
                key: 'Between70And75',
              },
              {
                label: HOME_DISCLOSURES_STRINGS.more_than_75_decibels,
                key: 'MoreThan75',
              },
            ]}
          />
          <Text className="mt-xl block font-semibold">
            {HOME_DISCLOSURES_STRINGS.accident_potential_zone}
          </Text>
          <Field
            validate={required}
            component={SelectorFinalFormAdapter}
            type={SELECTOR_TYPES.SMALLTEXTBUTTON}
            grid
            gridSize={GRID_SIZE.MEDIUM}
            name={`disclosures.types.${item.name}.accidentPotentialZone`}
            buttons={[
              { label: HOME_DISCLOSURES_STRINGS.none, key: 'None' },
              { label: HOME_DISCLOSURES_STRINGS.apz_2, key: 'APZ2' },
              { label: HOME_DISCLOSURES_STRINGS.apz_1, key: 'APZ1' },
              { label: HOME_DISCLOSURES_STRINGS.clear_zone, key: 'ClearZone' },
            ]}
          />
        </>
      )}

      {(item.name === 'HasBuildingHadABedBugInfestationDuringThePastYear' ||
        item.name === 'HasApartmentHadABedBugInfestationDuringThePastYear') && (
        <Field
          validate={required}
          component={YesNoFinalFormAdapter}
          alignCenter
          label={HOME_DISCLOSURES_STRINGS.bed_bugs_treated}
          name={`disclosures.types.${item.name}.treated`}
          fieldWrapperClassName="pt-xl"
        />
      )}

      {/* Stored inside the ConstructedBefore1978 disclosure, so the key is fixed, not from item.name. */}
      {item.name === 'PropertyYearBuilt' &&
        region === 'NewYorkCity' &&
        propertyYearBuilt?.toString().length === 4 &&
        Number(propertyYearBuilt) < 1978 && (
          <Field
            validate={required}
            component={YesNoFinalFormAdapter}
            alignCenter
            label={HOME_DISCLOSURES_STRINGS.constructed_before_1960}
            name="disclosures.types.ConstructedBefore1978.ConstructedBefore1960"
            fieldWrapperClassName="pt-xl"
          />
        )}

      {item.name === 'HasBuildingHadABedBugInfestationDuringThePastYear' && (
        <Field
          validate={required}
          placeholder={
            HOME_DISCLOSURES_STRINGS.building_bed_bugs_floors_where_it_occurred
          }
          component={InputFinalFormAdapter}
          name={`disclosures.types.${item.name}.floorsWhereItOccurred`}
        />
      )}

      {item.name === 'HasFireSprinklerSystem' && (
        <Field
          validate={composeValidators(required, dateValidationMMDDYY)}
          mask={maskMMDDYYYY}
          unmask={unmaskMMDDYYYY}
          placeholder={HOME_DISCLOSURES_STRINGS.last_serviced_on}
          component={DateInputFinalFormAdapter}
          name={`disclosures.types.${item.name}.lastServicedOn`}
        />
      )}

      {item.name === 'RadonTestInTheLast3Years' && (
        <>
          <Field
            validate={composeValidators(required, dateValidationMMDDYY)}
            mask={maskMMDDYYYY}
            unmask={unmaskMMDDYYYY}
            placeholder={HOME_DISCLOSURES_STRINGS.date_of_last_radon_test}
            component={DateInputFinalFormAdapter}
            name={`disclosures.types.${item.name}.DateOfLastRadonTestConducted`}
          />
          <Field
            validate={required}
            component={YesNoFinalFormAdapter}
            alignCenter
            label={
              HOME_DISCLOSURES_STRINGS.was_the_detected_radon_in_excess_of_4_0_pCi_L
            }
            name={`disclosures.types.${item.name}.RadonDetectedInExcessOf4`}
            fieldWrapperClassName="pt-xl"
          />
        </>
      )}

      {item.name === 'UsedToProduceMethamphetamine' && (
        <Field
          validate={required}
          component={YesNoFinalFormAdapter}
          alignCenter
          label={HOME_DISCLOSURES_STRINGS.was_it_remedied}
          name={`disclosures.types.${item.name}.usedToProduceMethamphetamineRemedied`}
          fieldWrapperClassName="pt-xl"
        />
      )}

      {/* NOTE: This is a workaround to avoid showing the field for Maryland and Massachusetts as we have follow-up questions for specific files in those markets. */}
      {item.name === 'LeadPaintTreatment' &&
        state !== 'MD' &&
        state !== 'MA' && (
          <div className="mt-lg">
            <Field
              name={`disclosures.types.${item.name}.files`}
              validate={validateMedia}
              component={NewMediaUploadAdapter}
              mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
              customLabel="Report"
              disableWebcam
              cleanOnMediaChange
              isSingleMedia
            />
          </div>
        )}

      {item.name === 'LeadPublicWaterServiceReplaced' &&
        'dateLabel' in item && (
          <div className="flex flex-col gap-xs">
            <Text className="mt-xl block font-semibold">{item.dateLabel}</Text>

            <Field
              mask={maskMMDDYYYY}
              unmask={unmaskMMDDYYYY}
              placeholder={item.datePlaceholder}
              component={DateInputFinalFormAdapter}
              name={`disclosures.types.${item.name}.${item.dateFieldName}`}
              validate={required}
              withMarginTop={false}
            />
          </div>
        )}

      {item.name === 'LeadPrivateWaterServiceReplaced' &&
        'dateLabel' in item && (
          <div className="flex flex-col gap-xs">
            <Text className="mt-xl block font-semibold">{item.dateLabel}</Text>
            <Field
              mask={maskMMDDYYYY}
              unmask={unmaskMMDDYYYY}
              placeholder={item.datePlaceholder}
              component={DateInputFinalFormAdapter}
              name={`disclosures.types.${item.name}.${item.dateFieldName}`}
              validate={required}
              withMarginTop={false}
            />
          </div>
        )}

      {item.name === 'LeadGovernmentPendingActionsAndNotices' && (
        <div className="mt-xs flex flex-col">
          <Field
            component={CheckboxFinalFormAdapter}
            alignCenter
            label="A notice of violation"
            name={`disclosures.types.${item.name}.ViolationNotice`}
            withMarginTop={false}
          />

          <Field
            component={CheckboxFinalFormAdapter}
            alignCenter
            label="A notice of lead-based paint hazards"
            name={`disclosures.types.${item.name}.LeadBasedPaintHazardNotice`}
            withMarginTop={false}
          />

          <Field
            component={CheckboxFinalFormAdapter}
            alignCenter
            label="An administrative order to eliminate lead-based paint hazards"
            name={`disclosures.types.${item.name}.EliminateLeadBasedPaintHazardsOrder`}
            withMarginTop={false}
          />

          <Field
            component={CheckboxFinalFormAdapter}
            alignCenter
            label="Civil fines, penalties, or fees related to failure to disclose lead water service lines and/or lead-bearing plumbing"
            name={`disclosures.types.${item.name}.CivilFinesOrFees`}
            withMarginTop={false}
          />

          <Field
            component={CheckboxFinalFormAdapter}
            alignCenter
            label="Other notices or orders related to lead-based paint"
            name={`disclosures.types.${item.name}.Other`}
            withMarginTop={false}
          />
          {showOtherNoticesDetail && (
            <div className="mt-lg">
              <Field
                placeholder="Notice / Order Type(s)"
                component={InputFinalFormAdapter}
                name={`disclosures.types.${item.name}.NoticeOrderTypeDetails`}
                validate={required}
              />
            </div>
          )}
        </div>
      )}

      {item.name === 'LeadServiceLines' && (
        <div className="mt-xs flex flex-col">
          <Field
            component={CheckboxFinalFormAdapter}
            alignCenter
            label="Public"
            name={`disclosures.types.${item.name}.publicSpace`}
            withMarginTop={false}
          />

          <Field
            component={CheckboxFinalFormAdapter}
            alignCenter
            label="Private"
            name={`disclosures.types.${item.name}.privateProperty`}
            withMarginTop={false}
          />
        </div>
      )}

      {item.name === 'LeadPaint' &&
        state === 'DC' &&
        propertyYearBuilt?.toString().length === 4 &&
        Number(propertyYearBuilt) < 1978 && (
          <div className="mt-lg flex flex-col gap-xs">
            <Text className="block font-semibold">
              Please list the location(s) where lead-based paint or hazards are
              present.
            </Text>
            <Field
              placeholder="Location"
              component={InputFinalFormAdapter}
              name={`disclosures.types.${item.name}.leadPaintPropertyLocations`}
              withMarginTop={false}
            />
          </div>
        )}

      {item.name === 'LeadDCWaterWebSite' && (
        <div className="mt-lg flex flex-col gap-lg">
          <div className="grid grid-cols-1 gap-lg md:grid-cols-2">
            <div className="flex flex-col gap-xs">
              <Field
                placeholder="Private Type"
                component={InputFinalFormAdapter}
                name={`disclosures.types.${item.name}.leadDCWaterWebSitePrivateType`}
                validate={required}
              />
            </div>

            <div className="flex flex-col gap-xs">
              <Field
                placeholder="Private Description"
                component={InputFinalFormAdapter}
                name={`disclosures.types.${item.name}.leadDCWaterWebSitePrivateDescription`}
                validate={required}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-lg md:grid-cols-2">
            <div className="flex flex-col gap-xs">
              <Field
                placeholder="Public Type"
                component={InputFinalFormAdapter}
                name={`disclosures.types.${item.name}.leadDCWaterWebSitePublicType`}
                validate={required}
              />
            </div>

            <div className="flex flex-col gap-xs">
              <Field
                placeholder="Public Description"
                component={InputFinalFormAdapter}
                name={`disclosures.types.${item.name}.leadDCWaterWebSitePublicDescription`}
                validate={required}
              />
            </div>
          </div>
        </div>
      )}

      {/* Washington DC — housing-code violations: counts + repeating violation
          rows (each with an optional notice file persisted via fileUniqueId). */}
      {item.name === 'HousingCodeViolations' && (
        <div className="mt-lg flex flex-col gap-lg">
          <Row>
            <Col xs={24} md={6}>
              <Field
                name={`disclosures.types.${item.name}.housingCodeNoticesLast12Months`}
                component={InputFinalFormAdapter}
                type="number"
                placeholder={
                  HOME_DISCLOSURES_STRINGS[
                    'housing_code.notices_last_12_months'
                  ]
                }
                validate={integer}
              />
            </Col>
            <Col xs={24} md={6}>
              <Field
                name={`disclosures.types.${item.name}.housingCodeUnabatedCount`}
                component={InputFinalFormAdapter}
                type="number"
                placeholder={
                  HOME_DISCLOSURES_STRINGS['housing_code.unabated_count']
                }
                validate={integer}
              />
            </Col>
          </Row>
          <StandardFieldArray
            fieldArrayName={`disclosures.types.${item.name}.housingCodeViolations`}
            mutators={form.mutators}
            FieldsComponent={HousingCodeViolationFields}
            values={formState.values}
            form={form}
            singleLine={false}
            showTrashButton
            iconButtonProps={{
              label: HOME_DISCLOSURES_STRINGS['housing_code.add_row'],
            }}
          />
        </div>
      )}

      {/* Washington DC — condo / co-op association status. */}
      {item.name === 'CondoOrCoopAssociation' && (
        <div className="mt-lg">
          <Field
            name={`disclosures.types.${item.name}.condoCoopAssociationType`}
            component={SelectorFinalFormAdapter}
            validate={required}
            type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
            className="grid grid-cols-1 gap-sm"
            buttonClassName="min-h-[110px] w-full py-md"
            buttons={[
              {
                label: HOME_DISCLOSURES_STRINGS['condo_coop.condominium'],
                key: 'Condominium',
                centerText: true,
              },
              {
                label: HOME_DISCLOSURES_STRINGS['condo_coop.cooperative'],
                subLabel:
                  HOME_DISCLOSURES_STRINGS['condo_coop.cooperative_sublabel'],
                key: 'Cooperative',
                centerText: true,
              },
              {
                label: HOME_DISCLOSURES_STRINGS['condo_coop.converting'],
                subLabel:
                  HOME_DISCLOSURES_STRINGS['condo_coop.converting_sublabel'],
                key: 'Converting',
                centerText: true,
              },
            ]}
          />
        </div>
      )}

      {/* Washington DC — indoor mold contamination (replaces generic Mold). */}
      {item.name === 'IndoorMoldContaminationDC' && (
        <Field
          placeholder={HOME_DISCLOSURES_STRINGS['indoor_mold.description']}
          component={InputFinalFormAdapter}
          name={`disclosures.types.${item.name}.comment`}
          validate={required}
        />
      )}

      {/* Washington DC — rent surcharge / pending petition (rent-stabilized). */}
      {item.name === 'RentSurchargeOrPendingPetition' && (
        <div className="mt-lg flex flex-col gap-lg">
          <div className="grid grid-cols-1 gap-lg md:grid-cols-2">
            <Field
              name={`disclosures.types.${item.name}.monthlySurchargeAmount`}
              component={InputFinalFormAdapter}
              mask={maskCurrency}
              unmask={unmaskNumber}
              placeholder={HOME_DISCLOSURES_STRINGS['rent_surcharge.amount']}
              withMarginTop={false}
            />
            {/* Optional: the MM/DD/YYYY mask constrains input; backend
                validates the surcharge fields when they apply. */}
            <Field
              name={`disclosures.types.${item.name}.surchargeExpiration`}
              component={DateInputFinalFormAdapter}
              mask={maskMMDDYYYY}
              unmask={unmaskMMDDYYYY}
              placeholder={
                HOME_DISCLOSURES_STRINGS['rent_surcharge.expiration']
              }
              withMarginTop={false}
            />
          </div>
          <Field
            placeholder={HOME_DISCLOSURES_STRINGS['rent_surcharge.comment']}
            component={InputFinalFormAdapter}
            name={`disclosures.types.${item.name}.comment`}
            withMarginTop={false}
          />
        </div>
      )}

      {/* Washington DC — how rent may be increased (rent-stabilized). */}
      {item.name === 'RentIncreaseMethod' && (
        <div className="mt-lg">
          <Field
            name={`disclosures.types.${item.name}.rentIncreaseMethod`}
            component={SelectorFinalFormAdapter}
            validate={required}
            type={SELECTOR_TYPES.MEDIUMTEXTBUTTON}
            className="grid grid-cols-1 gap-sm"
            buttonClassName="min-h-[110px] w-full py-md"
            buttons={[
              {
                label:
                  HOME_DISCLOSURES_STRINGS['rent_increase.once_per_12_months'],
                subLabel:
                  HOME_DISCLOSURES_STRINGS[
                    'rent_increase.once_per_12_months_sublabel'
                  ],
                key: 'OncePer12Months',
                centerText: true,
              },
              {
                label: HOME_DISCLOSURES_STRINGS['rent_increase.per_covenant'],
                subLabel:
                  HOME_DISCLOSURES_STRINGS[
                    'rent_increase.per_covenant_sublabel'
                  ],
                key: 'PerCovenantSubsidyOrHousingChoiceVoucher',
                centerText: true,
              },
            ]}
          />
        </div>
      )}

      {'filesRequired' in item &&
        item.filesRequired &&
        'fileLabel' in item &&
        item.fileLabel &&
        item.name !== 'LeadPaintTreatment' && (
          <div className="mt-lg">
            <Field
              name={`disclosures.types.${item.name}.files`}
              component={NewMediaUploadAdapter}
              mediaLabel={MEDIA_LABEL.FILE_UPLOAD}
              customLabel={item.fileLabel}
              disableWebcam
              cleanOnMediaChange
              isSingleMedia
            />
          </div>
        )}

      {'needsComment' in item && !!item.needsComment && (
        <Field
          placeholder={HOME_DISCLOSURES_STRINGS.describe}
          component={InputFinalFormAdapter}
          name={`disclosures.types.${item.name}.comment`}
        />
      )}

      {'needsLocation' in item &&
        !!item.needsLocation &&
        'locationLabel' in item &&
        'locationFieldName' in item && (
          <div className="flex flex-col gap-xs">
            <Text className="mt-xl block font-semibold">
              {item.locationLabel}
            </Text>

            <Field
              placeholder="Location"
              component={InputFinalFormAdapter}
              name={`disclosures.types.${item.name}.${item.locationFieldName}`}
              validate={required}
              withMarginTop={false}
            />
          </div>
        )}
    </Col>
  );
}
